/**
 * Find (Console V4 aiFindVals): the pure parts of the view, so they can be tested alone.
 * Every number comes from the server (GET /intelligence/find, /find/summary); nothing here
 * makes one up. Signals are the server's rule (find-v4.ts), money is MODELED.
 */
import type { FindLens, FindRow, FindSignal } from '@aithora/shared';

export const LENSES: [FindLens, string, string][] = [
  ['ranked', 'Ranked', 'Everything, best opportunity first'],
  ['distress', 'Distress', 'Falling occupancy or a 1-star rating, still falling'],
  ['sourcing', 'Sourcing', 'Quality gaps and turnarounds worth buying'],
  ['coverage', 'Coverage', 'Home health and hospice agencies near our buildings'],
];

/** Every state and DC, national first (2026-10-13: it offered only Texas and Florida). */
export const US_STATES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine',
  MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
  PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
  WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};
/** The type chips and the catalog type each one asks for. */
export const TYPE_CHIPS: Record<string, string | undefined> = { All: undefined, SNF: 'SNF', ALF: 'AL' };
export const BED_CHIPS = ['All', '<100', '100-199', '200+'];

/** The design's signal tag colours (no gold: Quality gap and Turnaround are blue). */
export const SIG: Record<FindSignal, [string, string]> = {
  Distress: ['#B3261E', 'rgba(179,38,30,.11)'],
  'Quality gap': ['#2F5C97', 'rgba(79,138,214,.13)'],
  Turnaround: ['#2F5C97', 'rgba(79,138,214,.13)'],
  Steady: ['#54606C', 'rgba(34,39,46,.07)'],
  Agency: ['#54606C', 'rgba(34,39,46,.07)'],
};

/** The grade from CMS stars (A 4-5, B 3, C 2 slate, D 1), or a dash with no rating. */
export function gradeOf(stars: number | null): [string, string] {
  if (stars === null) return ['–', '#54606C'];
  return stars >= 4 ? ['A', '#1F6B52'] : stars === 3 ? ['B', '#22272E'] : stars === 2 ? ['C', '#4A5866'] : ['D', '#B3261E'];
}

/** $M the design's way ("$12M", "$4.3M", a dash for nothing); $B past a thousand. */
export function money(x: number | null | undefined): string {
  if (x === null || x === undefined || !(x > 0)) return '–';
  if (x >= 1000) return '$' + (x / 1000).toFixed(x >= 10000 ? 0 : 1) + 'B';
  return '$' + (x >= 10 ? x.toFixed(0) : x.toFixed(1)) + 'M';
}

/** Polyline points for a sparkline w x h (the design's spark()). */
export function spark(arr: number[], w: number, h: number): string {
  const lo = Math.min(...arr);
  const hi = Math.max(...arr);
  const sp = hi - lo || 1;
  return arr.map((v, k) => ((k * w) / (arr.length - 1)).toFixed(1) + ',' + (h - 2 - ((v - lo) / sp) * (h - 4)).toFixed(1)).join(' ');
}

/**
 * The row's 12-month occupancy line: the catalog's own snapshots (facility_metric_history,
 * written each refresh, so the latest is today's figure); null under 2 points.
 */
export function occSeries(r: Pick<FindRow, 'occupancyTrend'>): number[] | null {
  const s = (r.occupancyTrend ?? []).map((p) => p.v).filter((v) => Number.isFinite(v));
  return s.length >= 2 ? s : null;
}

/**
 * The quick look's read when Atlas has not researched the row: our rule on the signal,
 * MODELED. It states only what the signal itself says.
 */
export function ruleVerdict(r: Pick<FindRow, 'signal' | 'unitType'>): [string, string, string] {
  if (r.unitType === 'agency' || r.signal === 'Agency') return ['Partner', '#1F6B52', 'An in-home agency. Assess it as a referral partner, not a purchase.'];
  switch (r.signal) {
    case 'Distress':
      return ['Watch', '#4A5866', 'Occupancy is still falling. Buy only after two flat quarters, or at a distressed price.'];
    case 'Quality gap':
      return ['Pursue', '#1F6B52', 'Full building with weak ratings. Staffing and quality fixes are where the value is.'];
    case 'Turnaround':
      return ['Pursue', '#1F6B52', 'Already recovering. Price will rise once the next rating lands.'];
    default:
      return ['Pass', '#54606C', 'Stable and steady. Nothing we would change.'];
  }
}

/** The stance colours for a researched verdict (pursue / watch / pass). */
export function stanceVerdict(stance: string, line: string): [string, string, string] {
  const s = stance.toLowerCase();
  const word = s.charAt(0).toUpperCase() + s.slice(1);
  return [word, s === 'pursue' ? '#1F6B52' : s === 'watch' ? '#4A5866' : '#54606C', line];
}

/** A research source's display name: the site from its URL, or the label as given. */
export function siteOf(src: string | undefined): string {
  if (!src) return 'Source';
  try {
    return new URL(src).hostname.replace(/^www\./, '');
  } catch {
    return src;
  }
}

/**
 * The payer revenue a building carries into the deal model ($M/yr), exactly as the server
 * sent it (financial/payer-mix.ts: its CMS cost report, else modeled or estimated). The
 * console keeps no split of its own. Rows with no known dollars are dropped; label is the
 * server's ('COST REPORT FY2024', 'MODELED', 'ESTIMATED'), null when the server sent none.
 */
export function serverPayers(r: Pick<FindRow, 'payers'>): { payers: { n: string; u: number }[]; label: string | null } {
  const p = r.payers;
  if (!p) return { payers: [], label: null };
  return { payers: p.rows.filter((x) => x.u != null && x.u > 0).map((x) => ({ n: x.n, u: x.u as number })), label: p.label };
}

/** Grid columns by the view's width (the design's fdCols). */
export function colsFor(w: number): { cols: string; wide: boolean; mid: boolean; narrow: boolean } {
  const wide = w >= 1180;
  const mid = w >= 1000;
  const narrow = w < 1100;
  const cols = wide
    ? '26px minmax(0,1fr) 30px 42px 44px 62px 30px 88px 58px 86px 118px'
    : mid
      ? '26px minmax(0,1fr) 30px 42px 44px 62px 30px 88px 118px'
      : '22px minmax(0,1fr) 28px 40px 88px';
  return { cols, wide, mid, narrow };
}

/** Thousands with commas. */
export const n0 = (x: number) => Math.round(x).toLocaleString('en-US');
